import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Table,
  Button,
  PageHeader,
  Result,
  Input,
  Popconfirm,
  Space,
  Typography,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import Auth from 'auth';
import { handleApiError, handleApiSuccess } from 'utils';
import {
  timeTrackerOpenTimersListService,
  timeTrackerOpenTimerStopService,
} from 'services/time_tracker';

const { Search } = Input;
const { Text } = Typography;

function userLabel(user) {
  if (!user) return 'Unknown user';
  const name =
    user.display_name ||
    [user.first_name, user.last_name].filter(Boolean).join(' ');
  return name || user.email || 'Unknown user';
}

function formatElapsed(startedAt, now) {
  if (!startedAt) return '00:00:00';
  const sec = Math.max(
    0,
    Math.floor((now - new Date(startedAt).getTime()) / 1000),
  );
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return [h, m, s].map((n) => String(n).padStart(2, '0')).join(':');
}

export default function TimeTrackerOpenTimersPage() {
  const history = useHistory();
  const isSuperAdmin = Number(Auth.getRole()) === 10;
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [stoppingId, setStoppingId] = useState(null);
  const [now, setNow] = useState(() => Date.now());

  const load = useCallback(() => {
    if (!isSuperAdmin) return;
    setLoading(true);
    timeTrackerOpenTimersListService()
      .then((res) => {
        setItems(res.data?.items || []);
      })
      .catch((err) => {
        handleApiError(err);
        setItems([]);
      })
      .finally(() => setLoading(false));
  }, [isSuperAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const stopTimer = (entryId) => {
    setStoppingId(entryId);
    return timeTrackerOpenTimerStopService(entryId)
      .then((res) => {
        handleApiSuccess(res);
        setItems((prev) => prev.filter((row) => row._id !== entryId));
      })
      .catch(handleApiError)
      .finally(() => setStoppingId(null));
  };

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => {
      const blob = [
        userLabel(item.user),
        item.user && item.user.email,
        item.reseller_name,
        item.project && item.project.project_name,
        item.task_description,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return blob.includes(q);
    });
  }, [items, search]);

  if (!isSuperAdmin) {
    return (
      <div style={{ padding: 24 }}>
        <Result
          status="403"
          title="Access denied"
          subTitle="Open timers are available to super admins only."
          extra={
            <Button type="primary" onClick={() => history.push('/')}>
              Go to Dashboard
            </Button>
          }
        />
      </div>
    );
  }

  const columns = [
    {
      title: 'User',
      key: 'user',
      render: (_, record) => (
        <div>
          <div>{userLabel(record.user)}</div>
          {record.user && record.user.email ? (
            <Text type="secondary">{record.user.email}</Text>
          ) : null}
        </div>
      ),
    },
    {
      title: 'Reseller',
      dataIndex: 'reseller_name',
      key: 'reseller',
      render: (name) => name || '—',
    },
    {
      title: 'Project',
      key: 'project',
      render: (_, record) =>
        (record.project && record.project.project_name) || 'No project',
    },
    {
      title: 'Description',
      dataIndex: 'task_description',
      key: 'description',
      ellipsis: true,
      render: (text) => text || '—',
    },
    {
      title: 'Started',
      dataIndex: 'started_at',
      key: 'started',
      render: (value) =>
        value ? moment(value).format('MMM D, YYYY h:mm A') : '—',
    },
    {
      title: 'Running',
      key: 'elapsed',
      render: (_, record) => formatElapsed(record.started_at, now),
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      render: (_, record) => (
        <Popconfirm
          title={`Stop the timer for ${userLabel(record.user)}? It will end as of now.`}
          okText="Stop"
          okButtonProps={{ danger: true }}
          cancelText="Cancel"
          onConfirm={() => stopTimer(record._id)}
        >
          <Button danger loading={stoppingId === record._id} size="small">
            Stop
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        ghost={false}
        title="Open timers"
        subTitle="Every timer that is still running, across all resellers"
        extra={
          <Space>
            <Search
              allowClear
              placeholder="Search user, reseller, project"
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 280 }}
            />
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
              Refresh
            </Button>
          </Space>
        }
      />
      <Table
        rowKey="_id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={{ pageSize: 50, hideOnSinglePage: true }}
        locale={{ emptyText: 'No open timers.' }}
      />
    </div>
  );
}
